import { useEffect, useState } from 'react';
import * as wasm from 'sched';
import { useDatabase } from './useDatabase';

export interface ProjectDetails {
  id: string;
  name: string;
  remoteReference?: wasm.RemoteReference;
}

/**
 * Hook to access project data
 */
export function useProject(projectId: string | undefined) {
  const { database, loading: databaseLoading } = useDatabase();
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !projectId) {
      setLoading(false);
      return;
    }

    try {
      const projects = database.projects();
      const projectObj = projects[projectId];

      if (!projectObj) {
        setError(`Project with ID ${projectId} not found`);
        setProject(null);
      } else {
        // Fetch project data asynchronously
        const fetchProjectData = async () => {
          try {
            // Get remote reference
            const remoteRef = await projectObj.remote_reference();

            // Set project with all data
            setProject({
              id: projectId,
              name: projectObj.name(),
              remoteReference: remoteRef
            });
            setError(null);
          } catch (err) {
            console.error('Error fetching project data:', err);
            // Still set the project with basic data
            setProject({
              id: projectId,
              name: projectObj.name(),
              remoteReference: undefined
            });
            setError(null);
          }
        };

        fetchProjectData();
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to fetch project details');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [database, projectId]);

  return { project, loading: loading || databaseLoading, error };
}
